export type Locale = 'de' | 'ar';
export type ThemeMode = 'system' | 'light' | 'dark';
export type WeightUnit = 'kg' | 'g';
export type SoilLevelId = 'light' | 'normal' | 'strong' | 'veryStrong';
export type TextileCategoryId =
  'whiteRobust' | 'colored' | 'delicate' | 'wool' | 'synthetic' | 'cottonLinen' | 'unsure';
export type TextileColor = 'white' | 'colored' | 'delicate' | 'unsure';
export type ConversionMode = 'school' | 'calibrated';
export type CalibrationStatus = 'notCalibrated' | 'calibrated' | 'manual';

export interface LocalizedText {
  de: string;
  ar?: string;
}

export interface AppSettings {
  id: 'app';
  appName: string;
  schoolName: string;
  language: Locale;
  theme: ThemeMode;
  defaultWaterHardnessId: string;
  waterHardnessLocked: boolean;
  welcomeDismissed: boolean;
  autoLockMinutes: number;
  createdAt: string;
  updatedAt: string;
}

export interface SoilLevel {
  id: SoilLevelId;
  name: LocalizedText;
  sortOrder: number;
}

export interface TextileCategory {
  id: TextileCategoryId;
  name: LocalizedText;
  sortOrder: number;
}

export interface Product {
  id: string;
  name: LocalizedText;
  category: LocalizedText;
  active: boolean;
  supportedSoilLevels: SoilLevelId[];
  instructions: LocalizedText[];
  safetyNotes: LocalizedText[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface DosageTable {
  id: string;
  productId: string;
  waterHardnessId: string;
  soilLevelId: SoilLevelId;
  mlPerKg: number;
}

export interface WaterHardness {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  minDh: number | null;
  maxDh: number | null;
  active: boolean;
  sortOrder: number;
}

export interface Container {
  id: string;
  name: LocalizedText;
  tareWeightG: number;
  active: boolean;
  isDefault: boolean;
  sortOrder: number;
}

export interface MeasuringCup {
  id: string;
  name: LocalizedText;
  capacityMl: number;
  emptyWeightG: number;
  active: boolean;
  sortOrder: number;
}

export interface ConversionConfiguration {
  productId: string;
  schoolGramsPerMl: number;
  calibratedGramsPerMl: number | null;
  activeConversionMode: ConversionMode;
  calibrationStatus: CalibrationStatus;
  calibrationDate: string | null;
  calibrationNote: string;
  updatedAt: string;
}

export interface CalibrationMeasurement {
  id: string;
  runId: string;
  productId: string;
  cupId: string | null;
  cupNameSnapshot: string;
  measuredMilliliters: number;
  measuredGrams: number;
  gramsPerMl: number;
  measuredAt: string;
}

export interface TemperatureGuidance {
  id: string;
  productId: string;
  textileCategoryIds: TextileCategoryId[];
  guidance: LocalizedText;
  active: boolean;
  sortOrder: number;
}

export interface RecommendationRule {
  id: string;
  textileCategoryId: TextileCategoryId;
  colorScope: TextileColor | 'any';
  productId: string | null;
  note: LocalizedText;
  active: boolean;
  priority: number;
}

export interface StainGuide {
  id: string;
  title: LocalizedText;
  shortDescription: LocalizedText;
  allowedTextileCategoryIds: TextileCategoryId[];
  allowedColors: TextileColor[];
  steps: LocalizedText[];
  warnings: LocalizedText[];
  recommendedWaterTemperature: LocalizedText | null;
  prohibitedActions: LocalizedText[];
  recommendedProductId: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityConfig {
  id: 'teacher-pin';
  algorithm: 'PBKDF2-SHA-256';
  algorithmVersion: 1;
  iterations: number;
  salt: string;
  derivedHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface RecoverySnapshot {
  id?: number;
  createdAt: string;
  reason: 'preImport';
  payload: BackupSchema;
}

/** Local-only marker for the last validated school configuration received from Cloudflare. */
export interface CloudSyncMetadata {
  id: 'school-config';
  revision: number;
  lastSyncedAt: string;
  source: 'cloud';
}

export interface BackupData {
  settings: AppSettings[];
  products: Product[];
  dosageTables: DosageTable[];
  waterHardness: WaterHardness[];
  containers: Container[];
  measuringCups: MeasuringCup[];
  conversionConfigurations: ConversionConfiguration[];
  calibrationMeasurements: CalibrationMeasurement[];
  temperatureGuidance: TemperatureGuidance[];
  recommendationRules: RecommendationRule[];
  stainGuides: StainGuide[];
}

export interface BackupSchema {
  format: 'waschpilot-school-backup';
  backupSchemaVersion: 1;
  databaseSchemaVersion: number;
  appVersion: string;
  exportedAt: string;
  data: BackupData;
}

export interface LaundryInput {
  totalWeight: number;
  unit: WeightUnit;
  containerTareG: number;
}

export interface DosageCalculation {
  dryLaundryKg: number;
  dosageMlPerKg: number;
  dosageMlExact: number;
  dosageMlRounded: number;
  gramsPerMl: number | null;
  dosageGramsExact: number | null;
  dosageGramsRounded: number | null;
}

export interface CupFilling {
  cupId: string;
  amountMl: number;
  isFull: boolean;
}

export interface CupRecommendation {
  cup: MeasuringCup;
  fillings: CupFilling[];
}

export type DomainErrorCode =
  | 'invalidWeight'
  | 'containerTooHeavy'
  | 'missingDosage'
  | 'unsupportedSoil'
  | 'productDisabled'
  | 'missingConversion'
  | 'noMeasuringCup';

export class DomainError extends Error {
  constructor(
    public readonly code: DomainErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'DomainError';
  }
}
